import { useState } from 'react';

import {
  Alert,
  AlertIcon,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Campaign,
  UpdateCampaignRequest,
  useToast,
  WinnerEligibility,
} from '@aslanpay/portal-common-lib';

import useUpdateCampaign from 'app/hooks/useUpdateCampaign';
import { getPointsValue } from 'app/utils/points';
import { formatCurrency } from 'app/utils/strings';

import {
  CAMPAIGN_TIMEZONE,
  isMomentAhead,
  SCHEDULE_MOMENTS,
  ScheduleMoment,
  scheduleProblems,
} from 'app/utils/campaigns/schedule';

import {
  fromRecurrenceRule,
  RecurrenceFormState,
  recurrenceError,
  toRecurrenceRule,
} from 'app/utils/campaigns/recurrence';

import AdvancedFields, {
  advancedError,
  AdvancedFormState,
  fromAdvanced,
  toAdvancedRequest,
} from '../AdvancedFields';
import RecurrenceFields from '../RecurrenceFields';
import { SCHEDULE_STEP_MINUTES } from '../CreateCampaign/useCreateCampaignForm';

const TIME_OPTIONS = Array.from({ length: (24 * 60) / SCHEDULE_STEP_MINUTES }, (_, index) => {
  const minutesFromMidnight = index * SCHEDULE_STEP_MINUTES;
  const hours = String(Math.floor(minutesFromMidnight / 60)).padStart(2, '0');

  return `${hours}:${String(minutesFromMidnight % 60).padStart(2, '0')}`;
});

interface EditCampaignModalProps {
  isOpen: boolean;
  onClose: () => void;
  campaign: Campaign;
}

/** An ISO instant as the date and time inputs hold it, in the reader's own timezone. */
const toLocal = (value: string | null | undefined) => {
  if (!value) return { date: '', time: '' };

  const date = new Date(value);
  const pad = (part: number) => String(part).padStart(2, '0');

  return {
    date: `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`,
    time: `${pad(date.getHours())}:${pad(date.getMinutes())}`,
  };
};

/**
 * Changing a campaign.
 * <p>
 * Once the scheme has started the award is the only thing the service will accept, so that is all the form
 * offers rather than letting a change be typed and refused.
 */
function EditCampaignModal({ isOpen, onClose, campaign }: EditCampaignModalProps) {
  const toast = useToast();
  const queryClient = useQueryClient();

  // The scheme's own rules are frozen once any of its cycles has started, which one cycle does not reveal, so the
  // read says. An older service that does not say is judged by this cycle instead.
  const settingsEditable = campaign.settingsEditable ?? campaign.status === 'PENDING';

  // Limiting the winner to a pride needs one to resolve against, which a single company-wide winner has not.
  const fansOut = campaign.scope === 'GROUP' || campaign.groupSelection !== 'NONE';

  const [name, setName] = useState(campaign.name);
  const [description, setDescription] = useState(campaign.description ?? '');
  const [awardPoints, setAwardPoints] = useState(
    campaign.awardPoints === null || campaign.awardPoints === undefined
      ? ''
      : String(campaign.awardPoints)
  );
  const [nominationsPerPerson, setNominationsPerPerson] = useState(
    String(campaign.nominationsPerPerson ?? 1)
  );
  const [valuesRequired, setValuesRequired] = useState(campaign.valuesRequired);
  const [allowWinnerWithoutNominations, setAllowWinnerWithoutNominations] = useState(
    campaign.allowWinnerWithoutNominations
  );
  // Held whether or not a winner may be picked without nominations, so turning that back on offers the
  // same choice again. A campaign that never said reads as anyone in the company.
  const [winnerEligibility, setWinnerEligibility] = useState<WinnerEligibility>(
    campaign.winnerEligibility ?? 'ALL_COMPANY'
  );
  const [schedule, setSchedule] = useState<Record<ScheduleMoment, { date: string; time: string }>>(
    () => ({
      nominationsOpenAt: toLocal(campaign.nominationsOpenAt),
      nominationsCloseAt: toLocal(campaign.nominationsCloseAt),
      reviewOpensAt: toLocal(campaign.reviewOpensAt),
      reviewClosesAt: toLocal(campaign.reviewClosesAt),
      announcementAt: toLocal(campaign.announcementAt),
    })
  );

  const [recurrence, setRecurrence] = useState<RecurrenceFormState>(() =>
    fromRecurrenceRule(campaign.recurring, campaign.recurrenceRule)
  );
  const [advanced, setAdvanced] = useState<AdvancedFormState>(() => fromAdvanced(campaign));

  const editableMoments = SCHEDULE_MOMENTS.filter((moment) =>
    isMomentAhead(moment.key, campaign.status)
  );

  const { mutate: update, isLoading } = useUpdateCampaign({
    onSuccess: () => {
      toast({
        position: 'top-right',
        status: 'success',
        title: 'Campaign updated',
        description: `${campaign.name} has been changed.`,
      });
      queryClient.invalidateQueries({ queryKey: ['fetchCampaigns'] });
      onClose();
    },
    onError: () =>
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Could not save the campaign',
        description: settingsEditable
          ? 'Nothing was saved. Check the details and try again.'
          : 'A campaign that has started accepts no change to the rules it runs by.',
      }),
  });

  const award = awardPoints === '' ? undefined : Number(awardPoints);
  const awardIsValid = awardPoints === '' || (Number.isInteger(award) && (award ?? 0) >= 0);

  const onSave = () => {
    // A PATCH, so only what the service will still accept is named. Naming a moment already reached, or a rule the
    // scheme has settled, is refused outright rather than ignored.
    const request: UpdateCampaignRequest = {
      awardPoints: award,
      name: name.trim(),
      description: description.trim(),
    };

    if (settingsEditable) {
      request.nominationsPerPerson = Number(nominationsPerPerson);
      request.valuesRequired = valuesRequired;
      request.allowWinnerWithoutNominations = allowWinnerWithoutNominations;

      if (allowWinnerWithoutNominations) {
        request.winnerEligibility = winnerEligibility;
      }

      request.timezone = CAMPAIGN_TIMEZONE;
      request.recurring = recurrence.recurring;
      request.recurrenceRule = toRecurrenceRule(recurrence);
      Object.assign(request, toAdvancedRequest(advanced));
    }

    editableMoments.forEach(({ key, optional }) => {
      const { date, time } = schedule[key];

      if (date && time) {
        request[key] = new Date(`${date}T${time}`).toISOString();
      } else if (!optional) {
        request[key] = undefined;
      }
    });

    update({ campaignId: campaign.id, request });
  };

  // The campaign the edit would leave behind, judged by the rules the service applies to it. A moment the
  // campaign has already passed is history, so its opening is only required ahead while it has yet to open.
  const problems = scheduleProblems(
    SCHEDULE_MOMENTS.reduce(
      (values, moment) => ({
        ...values,
        [moment.key]: schedule[moment.key].date
          ? `${schedule[moment.key].date}T${schedule[moment.key].time}`
          : '',
      }),
      {} as Record<ScheduleMoment, string>
    ),
    SCHEDULE_STEP_MINUTES,
    isMomentAhead('nominationsOpenAt', campaign.status)
  );

  const scheduleIsSet = editableMoments.every(({ key }) => !problems[key]);
  const recurrenceProblem = settingsEditable ? recurrenceError(recurrence) : undefined;
  const advancedProblem = settingsEditable ? advancedError(advanced) : undefined;
  const canSave =
    awardIsValid &&
    !isLoading &&
    name.trim().length > 0 &&
    scheduleIsSet &&
    !recurrenceProblem &&
    !advancedProblem;

  /**
   * Why the save is refused, in the order the form reads. The complaints sit beside the fields they are
   * about, which can be a long way above the button, so the footer says what is holding it rather than
   * leaving a dead button to be puzzled over.
   */
  const saveBlockedBecause = () => {
    if (!name.trim()) {
      return 'Give the campaign a name.';
    }

    if (!awardIsValid) {
      return 'The award has to be a whole number of points, or empty for no award.';
    }

    if (!scheduleIsSet) {
      const unsound = editableMoments.find(({ key }) => problems[key]);

      return unsound ? `${unsound.label}: ${problems[unsound.key]}` : undefined;
    }

    if (recurrenceProblem) {
      return recurrenceProblem;
    }

    return advancedProblem;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent data-testid="edit-campaign-modal">
        <ModalHeader>Edit campaign</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {!canSave && !isLoading && (
            <Alert
              status="error"
              position="sticky"
              top="0"
              zIndex="docked"
              mb="16px"
              data-testid="save-blocked-reason"
            >
              <AlertIcon />
              {saveBlockedBecause()}
            </Alert>
          )}
          <Flex flexDir="column" gap="16px">
            {!settingsEditable && (
              <Text
                textStyle="extraSmallRegular"
                color="brand.darkGray"
                data-testid="settings-fixed-notice"
              >
                This campaign has started, so the rules it runs by are fixed — anything else would
                move what nominations were already cast under. Its name, its award, and any moment
                of the schedule still ahead of it can all still change.
              </Text>
            )}

            <FormControl>
              <FormLabel>Name</FormLabel>
              <Input
                value={name}
                onChange={(event) => setName(event.target.value)}
                data-testid="edit-name-input"
              />
            </FormControl>
            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                data-testid="edit-description-input"
              />
            </FormControl>

            {settingsEditable && (
              <>
                <FormControl>
                  <FormLabel>Nominations per person, per run</FormLabel>
                  <Input
                    type="number"
                    min={1}
                    value={nominationsPerPerson}
                    onChange={(event) => setNominationsPerPerson(event.target.value)}
                    data-testid="edit-nominations-per-person"
                  />
                </FormControl>
                <Checkbox
                  isChecked={valuesRequired}
                  onChange={(event) => setValuesRequired(event.target.checked)}
                  data-testid="edit-values-required"
                >
                  A company value is required on every nomination
                </Checkbox>
                <Checkbox
                  isChecked={allowWinnerWithoutNominations}
                  onChange={(event) => setAllowWinnerWithoutNominations(event.target.checked)}
                  data-testid="edit-allow-winner-without-nominations"
                >
                  A winner may be picked who received no nominations
                </Checkbox>
                {allowWinnerWithoutNominations && (
                  <FormControl>
                    <FormLabel>Who may win</FormLabel>
                    <Select
                      value={winnerEligibility}
                      onChange={(event) =>
                        setWinnerEligibility(event.target.value as WinnerEligibility)
                      }
                      data-testid="edit-winner-eligibility"
                    >
                      <option value="ALL_COMPANY">Anyone in the company</option>
                      <option value="ALL_GROUP" disabled={!fansOut}>
                        Only people in that pride
                      </option>
                    </Select>
                  </FormControl>
                )}
                <RecurrenceFields
                  form={recurrence}
                  setField={(key, value) =>
                    setRecurrence((current) => ({ ...current, [key]: value }))
                  }
                  error={recurrenceProblem}
                />
                <AdvancedFields
                  form={advanced}
                  setField={(key, value) =>
                    setAdvanced((current) => ({ ...current, [key]: value }))
                  }
                />
                {advancedProblem && (
                  <Text
                    textStyle="extraSmallRegular"
                    color="text.error"
                    data-testid="edit-advanced-error"
                  >
                    {advancedProblem}
                  </Text>
                )}
              </>
            )}

            {editableMoments.length > 0 && (
              <Flex gap="16px" flexWrap="wrap">
                {editableMoments.map((input) => (
                  <Flex key={input.key} flexDir="column" flex="1" minW="240px">
                    <FormLabel>{input.label}</FormLabel>
                    <Flex gap="8px">
                      <Input
                        type="date"
                        flex="1"
                        value={schedule[input.key].date}
                        onChange={(event) =>
                          setSchedule((current) => ({
                            ...current,
                            [input.key]: { ...current[input.key], date: event.target.value },
                          }))
                        }
                        data-testid={`edit-${input.testId}`}
                      />
                      <Select
                        w="110px"
                        placeholder="Time"
                        value={schedule[input.key].time}
                        onChange={(event) =>
                          setSchedule((current) => ({
                            ...current,
                            [input.key]: { ...current[input.key], time: event.target.value },
                          }))
                        }
                        data-testid={`edit-${input.testId}-time`}
                      >
                        {TIME_OPTIONS.map((time) => (
                          <option key={time} value={time}>
                            {time}
                          </option>
                        ))}
                      </Select>
                    </Flex>
                    {problems[input.key] && (
                      <Text
                        textStyle="extraSmallRegular"
                        color="text.error"
                        mt="6px"
                        data-testid={`edit-${input.testId}-error`}
                      >
                        {problems[input.key]}
                      </Text>
                    )}
                  </Flex>
                ))}
              </Flex>
            )}

            {editableMoments.length > 0 && (
              <FormControl>
                <FormLabel>Timezone</FormLabel>
                <Input value={CAMPAIGN_TIMEZONE} isReadOnly data-testid="edit-timezone-input" />
              </FormControl>
            )}

            {editableMoments.length < SCHEDULE_MOMENTS.length && (
              <Text
                textStyle="extraSmallRegular"
                color="brand.darkGray"
                data-testid="moments-passed-notice"
              >
                A moment the campaign has already reached is settled, so it is not shown.
              </Text>
            )}

            <FormControl isInvalid={!awardIsValid}>
              <FormLabel>Award in points</FormLabel>
              <Input
                type="number"
                min={0}
                value={awardPoints}
                onChange={(event) => setAwardPoints(event.target.value)}
                placeholder="Leave empty for an award with no points"
                data-testid="edit-award-points"
              />
              {award !== undefined && awardIsValid && (
                <FormHelperText>
                  Worth {formatCurrency(getPointsValue(award), 'GBP')} to the winner.
                </FormHelperText>
              )}
              <FormHelperText data-testid="edit-award-winner-warning">
                A winner already picked keeps the amount held at their selection.
              </FormHelperText>
            </FormControl>
          </Flex>
        </ModalBody>
        <ModalFooter gap="12px">
          <Button variant="outline" onClick={onClose} data-testid="cancel-edit">
            Cancel
          </Button>
          <Button
            onClick={onSave}
            isDisabled={!canSave}
            isLoading={isLoading}
            data-testid="save-campaign"
          >
            Save changes
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default EditCampaignModal;
